import { useQueryClient } from '@tanstack/react-query';
import { FormEvent, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button';
import { FormLayout, Input, Label, Larger, Textarea } from '@/components/Forms';
import { NewDropdown } from '@/components/Forms/Dropdown';
import MultipleFileField from '@/components/Forms/Fields/MultipleFileField';
import FileField from '@/components/Forms/FileField';
import FormField from '@/components/Forms/FormField';
import ValidationMessage from '@/components/Forms/ValidationMessage';
import InfoTooltip from '@/components/InfoTooltip';
import Text from '@/components/Text';
import useAccountNumbersOptions from '@/hooks/useAccountNumbersOptions';
import useAlerts from '@/hooks/useAlerts';
import useCompaniesOptions from '@/hooks/useCompaniesOptions';
import useDepartmentsOptions from '@/hooks/useDepartmentsOptions';
import InvoicesBankField from '@/pages/InvoiceList/components/InvoicesBankField';
import InvoicesReceiverType from '@/pages/InvoiceList/components/InvoicesReceiverType';
import InvoicesRecieverField from '@/pages/InvoiceList/components/InvoicesRecieverField';
import { api } from '@/utils/api';
import { currencies } from '@/utils/data';
import { selectablePaymentMethods } from '@/utils/data/payments';
import { PaymentMethod } from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';

import AddInvoiceDropdown from './AddInvoiceDropdown';
import GpmCheckboxFiekd from './GpmCheckboxFiekd';
import ProformaCheckboxField from './ProformaCheckboxField';

export type FormDataT = {
  receiver: string;
  external_id: string;
  department_id: number | null;
  company_id: number | null;
  finance_account_number_id: number | null;
  currency: 'USD' | 'EUR' | 'GBP';
  amount: number;
  file: File | null;
  proforma_file: File | null;
  is_proforma: boolean;
  comment: string;
  bank_account_id: number;
  paypal_email: string;
  payment_method: PaymentMethod;
  payment_purpose: string;
  gpm: boolean;
  serial_number: string;
  invoice_number: string;
  invoice_date: string;
};

const Box = styled.div`
  background: #ffffff;
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  padding: 24px;
`;

const StyledInput = styled(Input)`
  border-bottom-right-radius: 0;
  border-top-right-radius: 0;
  padding-left: 34px;
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  ::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const InputHolder = styled.div`
  position: relative;
  width: 100%;
`;

const Currency = styled(Text)`
  position: absolute;
  left: 16px;
  top: 12px;
`;

const ButtonHolder = styled.div`
  margin-top: 24px;
  text-align: center;
`;

const MAX_ADDITIONAL_FILE_SIZE = 5 * 1024 * 1024;
const ADDITIONAL_FILES_ACCEPT =
  '.doc, .docx,application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/pdf';

const StyledButton = styled(Button)`
  padding: 0 60px;
  height: 56px;
`;

const StyledTextarea = styled(Textarea)`
  height: 118px;
`;

const InputsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-column-gap: 12px;
`;

const AddInvoiceForm = () => {
  const navigate = useNavigate();

  const [shouldDisableType, setShouldDesableType] = useState(false);
  const [additionalFiles, setAdditionalFiles] = useState<File[]>([]);

  const queryClient = useQueryClient();

  const alerts = useAlerts();

  const { data: accountNumberOptions = [] } = useAccountNumbersOptions();

  const { data: departmentOptions = [] } = useDepartmentsOptions();

  const filteredDepartmentOptions = departmentOptions.filter(
    (dept) => !dept.hide_from_dropdowns,
  );

  const { data: allCompanyOptions = [] } = useCompaniesOptions();

  const companyOptions = allCompanyOptions.filter(
    (company) => !company.hide_from_dropdowns,
  );

  const methods = useForm<FormDataT>({
    defaultValues: {
      finance_account_number_id: null,
      department_id: null,
      company_id: null,
      currency: 'EUR',
      receiver: '',
      gpm: false,
      is_proforma: false,
      payment_method: PaymentMethod.Bank,
      paypal_email: '',
    },
  });

  const {
    register,
    control,
    handleSubmit,
    setError,
    clearErrors,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = methods;

  const currentCurrency = useWatch({
    control,
    name: 'currency',
  });

  const paymentMethod = useWatch({
    name: 'payment_method',
    control,
  });

  const isGpm = useWatch({
    name: 'gpm',
    control,
  });

  const isProforma = useWatch({
    name: 'is_proforma',
    control,
  });

  const renderCurrentCurrency = () => {
    switch (currentCurrency) {
      case 'USD':
        return '$';
      case 'EUR':
        return '€';
      case 'GBP':
        return '£';
      default:
        return '€';
    }
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async ({ ...data }) => {
      try {
        const formData = new FormData();
        formData.append('receiver', data.receiver);
        if (data.finance_account_number_id) {
          formData.append(
            'finance_account_number_id',
            data.finance_account_number_id.toString(),
          );
        }
        formData.append('currency', data.currency);
        formData.append('amount', data.amount.toString());
        formData.append('serial_number', data.serial_number);
        formData.append('invoice_number', data.invoice_number);
        formData.append('invoice_date', data.invoice_date);
        formData.append('payment_method', data.payment_method);

        if (data.department_id) {
          formData.append('department_id', data.department_id.toString());
        }

        if (data.company_id) {
          formData.append('company_id', data.company_id.toString());
        }

        if (data.payment_purpose) {
          formData.append('payment_purpose', data.payment_purpose);
        }

        if (data.paypal_email && data.payment_method === PaymentMethod.Paypal) {
          formData.append('paypal_email', data.paypal_email);
        }

        if (data.gpm) {
          formData.append('gpm', '1');
        }

        if (data.bank_account_id) {
          formData.append('bank_account_id', data.bank_account_id.toString());
        }

        if (data.is_proforma) {
          formData.append('is_proforma', '1');

          if (data.proforma_file) {
            formData.append('proforma_file', data.proforma_file);
          }
        } else if (data.file) {
          formData.append('file', data.file);
        }

        additionalFiles.forEach((file) => {
          formData.append('additional_files[]', file);
        });

        formData.append('comment', data.comment);

        const response = await api.post<
          FormData,
          { id: number; member_recently_created: boolean; member_id: number }
        >('/v1/payments/invoices', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        queryClient.invalidateQueries(['invoices_members'], {
          exact: true,
        });

        queryClient.invalidateQueries(['invoices_list'], {
          exact: true,
        });

        reset();
        setAdditionalFiles([]);

        if (response.member_recently_created) {
          const confirmed = await alerts.confirm(
            'A new member was successfully created',
            'Would you like to complete profile information now?',
          );

          if (confirmed) {
            navigate(`/general/invoices/receiver-list`, {
              state: { newMemberId: response.member_id },
            });
            alerts.close();
          }
        }

        return alerts.success('Invoice has been added succesfully');
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts);
      }
    })(e);
  };

  return (
    <form onSubmit={onSubmit} autoComplete="off">
      <Box>
        <FormProvider {...methods}>
          <FormLayout>
            <div>
              <InvoicesRecieverField
                control={control}
                name="receiver"
                setValue={setValue}
                setShouldDesableType={setShouldDesableType}
                methods={selectablePaymentMethods}
              />
              {!!errors.receiver && (
                <ValidationMessage>{errors.receiver.message}</ValidationMessage>
              )}
            </div>
            <div>
              <InvoicesReceiverType
                paymentMethod={paymentMethod}
                setValue={setValue}
                shouldDisableType={shouldDisableType}
                methods={selectablePaymentMethods}
              />
            </div>
            <div>
              {paymentMethod === PaymentMethod.Bank ? (
                <InvoicesBankField
                  name="bank_account_id"
                  control={control}
                  setValue={setValue}
                />
              ) : (
                <FormField<FormDataT, string>
                  name="paypal_email"
                  control={control}
                  label="Receiver Email"
                  placeholder="Add email"
                />
              )}
            </div>
            <div>
              <Label htmlFor="department_id">
                Department
                <InfoTooltip
                  width={270}
                  title="Department"
                  content="Select the department to which expenses must be assigned."
                  position="bottom-center"
                />
              </Label>
              <AddInvoiceDropdown
                options={filteredDepartmentOptions}
                setValue={setValue}
                name="department_id"
                control={control}
              />
              {!!errors.department_id && (
                <ValidationMessage>
                  {errors.department_id.message}
                </ValidationMessage>
              )}
            </div>
            <div>
              <Label htmlFor="company_id">
                Company
                <InfoTooltip
                  width={270}
                  title="Company"
                  content="Select the company to which expenses must be assigned."
                  position="bottom-center"
                />
              </Label>
              <AddInvoiceDropdown
                options={companyOptions}
                setValue={setValue}
                name="company_id"
                control={control}
              />
              {!!errors.company_id && (
                <ValidationMessage>
                  {errors.company_id.message}
                </ValidationMessage>
              )}
            </div>
            <InputsGrid>
              <div>
                <Label htmlFor="serialNumber">Serial Number</Label>
                <Input
                  id="serialNumber"
                  placeholder="Add Number"
                  {...register('serial_number')}
                />
              </div>
              <div>
                <Label htmlFor="invoiceNumber">Invoice Number</Label>
                <Input
                  id="invoiceNumber"
                  placeholder="Add Number"
                  {...register('invoice_number')}
                  $hasError={!!errors.invoice_number}
                />
                {!!errors.invoice_number && (
                  <ValidationMessage>
                    {errors.invoice_number.message}
                  </ValidationMessage>
                )}
              </div>
              <div>
                <Label htmlFor="invoiceDate">Invoice Date</Label>
                <Input
                  id="invoiceDate"
                  placeholder="Add Date"
                  {...register('invoice_date')}
                  $hasError={!!errors.invoice_date}
                />
                {!!errors.invoice_date && (
                  <ValidationMessage>
                    {errors.invoice_date.message}
                  </ValidationMessage>
                )}
              </div>
            </InputsGrid>
            <div>
              <Label htmlFor="financeAccountNumber">
                Finance account number
                <InfoTooltip
                  width={270}
                  title="Finance account number"
                  content="Accounting code that indicates the type of expenses"
                  position="bottom-center"
                />
              </Label>
              <AddInvoiceDropdown
                options={accountNumberOptions}
                setValue={setValue}
                name="finance_account_number_id"
                control={control}
              />
              {!!errors.finance_account_number_id && (
                <ValidationMessage>
                  {errors.finance_account_number_id.message}
                </ValidationMessage>
              )}
            </div>
            <div>
              <Label htmlFor="amount">Amount</Label>
              <InputHolder>
                <StyledInput
                  id="amount"
                  placeholder="Add amount"
                  {...register('amount')}
                  $hasError={!!errors.amount}
                />
                <Currency $type="captionSemibold">
                  {renderCurrentCurrency()}
                </Currency>
              </InputHolder>
              {!!errors.amount && (
                <ValidationMessage>{errors.amount.message}</ValidationMessage>
              )}
            </div>
            <div>
              <Label>Currency</Label>
              <NewDropdown
                options={currencies}
                control={control}
                name="currency"
              />
              {!!errors.currency && (
                <ValidationMessage>{errors.currency.message}</ValidationMessage>
              )}
            </div>
            <div>
              <Label htmlFor="purposeOfPayment">The Purpose of Payment</Label>
              <Input
                id="purposeOfPayment"
                placeholder="Add purpose"
                {...register('payment_purpose')}
                $hasError={!!errors.payment_purpose}
              />
              {!!errors.payment_purpose && (
                <ValidationMessage>
                  {errors.payment_purpose.message}
                </ValidationMessage>
              )}
            </div>
            <GpmCheckboxFiekd isGpm={isGpm} setValue={setValue} />
            <ProformaCheckboxField
              isProforma={isProforma}
              setValue={setValue}
              resetFiles
            />
            <Larger>
              <Label htmlFor="comment">Comments</Label>
              <StyledTextarea
                id="comment"
                placeholder="Add comments"
                {...register('comment')}
              />
            </Larger>
            <Larger>
              <Label>
                {isProforma ? 'Attach proforma file' : 'Attach file'}
              </Label>
              <FileField
                key={isProforma ? 'proforma_file' : 'file'}
                name={isProforma ? 'proforma_file' : 'file'}
                fileType="other"
                notes="PDF, doc, docx, xls, xlsx / Size: Up to 2MB"
                accept=".doc, .docx,application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/pdf"
              />
            </Larger>
            <Larger>
              <MultipleFileField
                label="Additional files"
                value={additionalFiles}
                onChange={setAdditionalFiles}
                accept={ADDITIONAL_FILES_ACCEPT}
                maxSize={MAX_ADDITIONAL_FILE_SIZE}
                notes="PDF, doc, docx, xls, xlsx / Size: Up to 5MB each"
              />
            </Larger>
          </FormLayout>
        </FormProvider>
      </Box>
      <ButtonHolder>
        <StyledButton
          disabled={isSubmitting}
          loading={isSubmitting}
          type="submit"
        >
          Send
        </StyledButton>
      </ButtonHolder>
    </form>
  );
};

export default AddInvoiceForm;
